import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import AddressMap from "@/components/AddressMap";
import { AddressFields } from "@/components/AddressFields";

export default function Onboarding() {
  const { refreshMe } = useAuth();
  const { t } = useI18n();
  const [role, setRole] = useState("pool_owner");
  const [munis, setMunis] = useState([]);
  const [addr, setAddr] = useState({ street: "", postal_code: "", city: "" });
  const [form, setForm] = useState({
    municipality_name: "",
    municipality_id: "",
    address: "",
    lat: null,
    lng: null,
    pool_area: "",
    pool_depth: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      role,
      address: form.address || null,
      lat: form.lat,
      lng: form.lng,
    };
    if (role === "municipality") payload.municipality_name = form.municipality_name;
    else {
      Object.assign(payload, addr);
      payload.pool_area = form.pool_area ? parseFloat(form.pool_area) : null;
      payload.pool_depth = form.pool_depth ? parseFloat(form.pool_depth) : null;
    }
    try {
      await api.post("/auth/onboard", payload);
      await refreshMe();
      toast.success("Setup ✓");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl py-10">
      <div className="label-caps mb-2">Setup</div>
      <h1 className="mb-6 text-3xl font-bold tracking-tight">{t("create_account")}</h1>
      <Card className="border-border">
        <CardContent className="p-6">
          <form className="space-y-4" onSubmit={submit}>
            <div>
              <Label>{t("role")}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="onb-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pool_owner">{t("pool_owner")}</SelectItem>
                  <SelectItem value="municipality">{t("municipality")}</SelectItem>
                  <SelectItem value="utility">{t("utility")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {role === "municipality" ? (
              <div>
                <Label>{t("municipality_name")}</Label>
                <Input required value={form.municipality_name} onChange={(e) => setForm({ ...form, municipality_name: e.target.value })} data-testid="onb-muni-name" />
              </div>
            ) : role === "pool_owner" ? (
              <>
                <AddressFields value={addr} onChange={setAddr} t={t} prefix="onb" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>{t("pool_area")}</Label>
                    <Input type="number" step="0.1" value={form.pool_area} onChange={(e) => setForm({ ...form, pool_area: e.target.value })} data-testid="onb-area" />
                  </div>
                  <div>
                    <Label>{t("pool_depth")}</Label>
                    <Input type="number" step="0.1" value={form.pool_depth} onChange={(e) => setForm({ ...form, pool_depth: e.target.value })} data-testid="onb-depth" />
                  </div>
                </div>
              </>
            ) : null}

            <div>
              <Label>{role === "pool_owner" ? t("map_location_optional") : t("address")}</Label>
              <AddressMap
                address={form.address}
                lat={form.lat}
                lng={form.lng}
                autoQuery={role === "pool_owner" && addr.street.trim() && /^\d{4,5}$/.test(addr.postal_code.trim()) && addr.city.trim() ? `${addr.street.trim()}, ${addr.postal_code.trim()} ${addr.city.trim()}` : ""}
                onChange={({ address, lat, lng }) => setForm((f) => ({ ...f, address, lat, lng }))}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="onb-submit">
              {loading ? "…" : t("save")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
